import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { demoAuthService } from '../services/demoAuthService';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await demoAuthService.login(email, password);

    if (result.success) {
      // Clean up any legacy extended session flags (CJIS 5.5.5 compliance)
      localStorage.removeItem('extendedSession');
      navigate('/');
    } else {
      setError(result.error || 'Login failed');
    }

    setLoading(false);
  };

  const handleDemoLogin = () => {
    setEmail('demo@a3.demo.com');
    setPassword('a3-demo-user');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Container maxWidth="md">
        <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
          {/* Logo */}
          <Box
            component="img"
            src="/logo.png"
            alt="GovIQ CallScore"
            sx={{
              height: 80,
              filter: 'brightness(0) invert(1)', // Make logo white
            }}
          />

          {/* Main Login Card */}
          <Card
            sx={{
              width: '100%',
              maxWidth: 400,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                textAlign="center"
                fontWeight="bold"
                color="primary"
              >
                GovIQ CallScore
              </Typography>

              <Typography
                variant="subtitle1"
                textAlign="center"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                Public Safety Call Analysis Platform
              </Typography>

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="email"
                  autoFocus
                />

                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />

                {error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ mt: 3, mb: 2, py: 1.5 }}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>

              {/* Demo Login Buttons */}
              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Demo Access
                </Typography>
              </Divider>

              <Button
                variant="outlined"
                size="medium"
                fullWidth
                onClick={handleDemoLogin}
                sx={{ mb: 2 }}
              >
                Try Demo
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};
